import {
	createContext,
	useContext,
	useState,
	useEffect,
	useMemo,
	useCallback,
} from 'react';

import { useApp } from '../../../app/AppContext.jsx';

import { fetchLocalJSON } from '../../../shared/utils/fetchLocalJSON.js';

import { useCatalogCapabilities } from '../hooks/useCatalogCapabilities.js';
import { useColorMode } from '../hooks/useColorMode.js';
import { useFilters } from '../hooks/useFilters.js';
import { useProducts } from '../hooks/useProducts.js';
import { buildTiersData } from '../utils/buildTiersData.js';
import { resolveAdjacentProductsBySort } from '../utils/filterProducts.js';

import { buildProductAreaDeciles } from '../../../shared/utils/productAreaDeciles.js';
import {
	isDisplayable,
	isProductAccessible,
} from '../../../shared/utils/productUtils.js';
import { COLOR_MODE } from '../../../shared/constants/colorMode.js';
import { SECTION_ID } from '../../../shared/constants/sectionIds.js';
import { buildBuildingsData } from '../utils/buildBuildingsData.js';

const CATALOG_FILTERS_SCROLL_OPTIONS = {
	offsetCssVar: '--bim-section-padding',
};

// ── Context ────────────────────────────────────────────────────────────────────

export const CatalogContext = createContext(null);

// ── Provider ───────────────────────────────────────────────────────────────────

/**
 * Domain-level context provider for the catalog.
 *
 * Aggregates:
 *   - `useProducts()` — product + building data fetch lifecycle
 *   - `useFilters()` — filter/sort state and derived filteredProducts
 *   - `compartments.json` — optional; failure exposes `compartmentsError`
 *     and disables the external_areas filter (the rest of the catalog works normally)
 *
 * Must be mounted inside `AppContextProvider` (reads `windowData` and `panelSettings`).
 * Should wrap catalog domain components, NOT be placed in `AppContextProvider`.
 */
export function CatalogContextProvider({ children }) {
	const {
		activateSectionHandler,
		appCapabilities,
		isFavorite,
		isMobile,
		panelSettings,
		projectConfig,
		windowData,
	} = useApp();

	// ── Compartments (optional) ──────────────────────────────────────────────

	const [compartments, setCompartments] = useState({});
	const [compartmentsError, setCompartmentsError] = useState(null);

	useEffect(() => {
		const dataUrl = windowData?.projectDataUrl;
		if (!dataUrl) return;

		let cancelled = false;

		fetchLocalJSON(
			`${dataUrl}website/compartments.json`,
			'CatalogContext: compartments.json',
		)
			.then((data) => {
				if (cancelled) return;
				if (data) {
					setCompartments(data);
				} else {
					setCompartmentsError(
						new Error('compartments.json not found or empty'),
					);
				}
			})
			.catch((err) => {
				if (cancelled) return;
				setCompartmentsError(
					err instanceof Error ? err : new Error(String(err)),
				);
			});

		return () => {
			cancelled = true;
		};
	}, [windowData]);

	// ── Products ─────────────────────────────────────────────────────────────

	const {
		products,
		// buildings,
		interiorsData,
		interiorsError,
		rawBuildings,
		rawCmsData,
		productsStatus,
		productsError,
	} = useProducts();

	// ── Resolved panel settings derived flags and context ─────────────────────

	const tiersSettings = useMemo(
		() => panelSettings?.tiers_settings?.tiers ?? [],
		[panelSettings],
	);
	//TODO: tiers with flag !is_active shouldn't be included in filters range?

	const filtersConfig = useMemo(() => {
		return {
			initialFiltering: panelSettings?.catalog_initial_filtering ?? {},
			initialSortingLevels:
				panelSettings?.catalog_initial_sorting?.parameters ?? [],
			reservedUnitsAccessible: appCapabilities?.reservedUnitsAccessible ?? true,
			soldUnitsAccessible: appCapabilities?.soldUnitsAccessible ?? true,
		};
	}, [panelSettings, appCapabilities]);

	// ── Derived tiers ─────────────────────────────────────────────────────────
	// Filters out technical-only top tiers (roof etc.) — keeps all floors up to
	// the highest tier on which any product has a compartment.

	const tiers = useMemo(
		() => buildTiersData(rawBuildings, products, tiersSettings),
		[rawBuildings, products, tiersSettings],
	);

	// ── Enhanced buildings ─────────────────────────────────────────────────────────
	// Buildings data with added phasing config and isActive flag
	const buildings = useMemo(
		() => buildBuildingsData(rawBuildings, tiers, panelSettings),
		[rawBuildings, panelSettings, tiers],
	);

	//TODO: buildings with flag !isActive shouldn't be included in filters range?

	// ── Filters ──────────────────────────────────────────────────────────────

	const filters = useFilters({
		appCapabilities,
		products,
		buildings,
		compartments,
		config: filtersConfig,
		isFavorite,
	});
	const { catalogProducts, filteredProducts, fullFiltersRange } = filters;

	// ── Color mode (shared between TiersView / ModelView) ──────────────────────
	const {
		activeColorModes,
		defaultColorMode,
		reservedUnitsAccessible,
		soldUnitsAccessible,
	} = appCapabilities;

	const { colorMode, setColorMode, availableColorModes } = useColorMode({
		activeColorModes,
		defaultColorMode,
	});

	// ── Catalog filter UI bridge (desktop panel / mobile sheet) ───────────────

	const [isFiltersSheetOpen, setIsFiltersSheetOpen] = useState(false);
	const [isDesktopFiltersExpanded, setIsDesktopFiltersExpanded] =
		useState(false);

	const openFiltersUi = useCallback(() => {
		activateSectionHandler(SECTION_ID.CATALOG, {
			scrollTarget: SECTION_ID.CATALOG,
			scrollOptions: CATALOG_FILTERS_SCROLL_OPTIONS,
		});

		if (isMobile) {
			setIsFiltersSheetOpen(true);
			return;
		}

		setIsDesktopFiltersExpanded(true);
	}, [activateSectionHandler, isMobile]);

	const closeFiltersUi = useCallback(() => {
		if (isMobile) {
			setIsFiltersSheetOpen(false);
			return;
		}

		setIsDesktopFiltersExpanded(false);
	}, [isMobile]);

	const restrictedProducts = useMemo(() => {
		return filteredProducts.filter((product) =>
			isProductAccessible(product, {
				reservedUnitsAccessible,
				soldUnitsAccessible,
			}),
		);
	}, [filteredProducts,
    reservedUnitsAccessible,
				soldUnitsAccessible,
  ]);

	const getAdjacentProducts = useCallback(
		(currentProduct) => {
			return resolveAdjacentProductsBySort(restrictedProducts, currentProduct);
		},
		[restrictedProducts],
	);

	const colorModeDeciles = useMemo(() => {
		if (colorMode !== COLOR_MODE.COMPARE) return null;
		return buildProductAreaDeciles(
			products.filter((p) => isDisplayable(p.visibility)),
		);
	}, [colorMode, products]);

	const catalogCapabilities = useCatalogCapabilities({
		appCapabilities,
		fullFiltersRange,
		products,
		projectConfig,
		panelSettings,
	});

	// ── Context value ─────────────────────────────────────────────────────────

	const value = {
		// Products
		products,
		buildings,
		interiorsData,
		rawCmsData,
		tiers,
		productsStatus,
		productsError,
		interiorsError,
		// Compartments
		compartments,
		compartmentsError,
		// Capabilities
		catalogCapabilities,
		// Filters & sort (all useFilters returns)
		...filters,
		catalogProducts,
		getAdjacentProducts,
		// Color mode
		colorMode,
		setColorMode,
		availableColorModes,
		colorModeDeciles,
		// Filters UI bridge (Hero -> Catalog Filters)
		isFiltersSheetOpen,
		setIsFiltersSheetOpen,
		isDesktopFiltersExpanded,
		setIsDesktopFiltersExpanded,
		openFiltersUi,
		closeFiltersUi,
		// Derived flags
		isMultiBuilding: buildings.length > 1, //TODO: move to catalogCapabilities?
	};

	return (
		<CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
	);
}

// ── Consumer hook ──────────────────────────────────────────────────────────────

/**
 * Hook to consume the CatalogContext.
 * Must be used within `CatalogContextProvider`.
 *
 * @returns {object} CatalogContext value
 */
export function useCatalog() {
	const ctx = useContext(CatalogContext);
	if (!ctx)
		throw new Error('useCatalog must be used within CatalogContextProvider');
	return ctx;
}
